import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useInvoiceDetailsQuery, setInvoiceDetailsQuery, useInvoiceStylesQuery, usePaymentHistory, setPaymentHistory } from '~/lib/Queries';
import useBusinessNavigate from '~/lib/useBusinessNavigate';
import useBusinessPostApi from '~/lib/useBusinessPostApi';
import * as Toast from '~/lib/Toast';
import Loading from '~/components/Loading';
import PageHeading from '~/components/PageHeading';
import SectionPicker from '~/components/SectionPicker';
import { EyeIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import AuditLogs from '~/components/AuditLogs';
import { ObjectTypes } from '~/lib/ObjectTypes';
import { InvoiceProvider } from '~/lib/InvoiceContext';
import Section from '~/components/forms/Section';
import AddQuestionPanel from '~/components/forms/AddQuestionPanel';
import Questions from '~/lib/Questions';
import EditableQuestion from '~/components/forms/EditableQuestion';
import InvoiceEditQuestionPanel from '~/components/forms/InvoiceEditQuestionPanel';
import SortableList from '~/components/SortableList';
import SendInvoiceModal from '~/components/invoices/SendInvoiceModal';
import InvoiceStatusBadge from '~/components/invoices/InvoiceStatusBadge';
import FormBackgroundContainer from '~/components/forms/FormBackgroundContainer';
import PageContainer from '~/public/components/PageContainer';
import AdminPaymentPanel from '~/components/payments/AdminPaymentPanel';
import PaymentHistory from '~/components/payments/PaymentHistory';
import SectionHeading from '~/components/SectionHeading';
import useBlockUnsavedChanges from '~/lib/useBlockUnsavedChanges.js';
import WarningMessageBlock from '~/components/WarningMessageBlock.jsx';

const ActivitySection = ({ invoiceId, invoice }) => {
    const {
        data,
        isLoading,
        refetch: refetchPaymentHistory,
    } = usePaymentHistory(ObjectTypes.INVOICE, invoiceId);

    const history = data?.payments || [];

    return (
        <div className="max-w-2xl">
            <div className="mt-8">
                {history?.length > 0 && <div className="panel-label">Payments</div>}


                {isLoading ? (
                    <Loading />
                ) : (
                    <PaymentHistory
                        history={history}
                        codes={data?.codes || []}
                        savedPaymentMethods={data?.savedPaymentMethods}
                        totalPrice={invoice?.total || 0}
                        onUpdate={() => {
                            refetchPaymentHistory();
                        }}
                    />
                )}
            </div>

            <div className="panel-label mt-8">Activity Log</div>
            <div className="panel">
                <AuditLogs type={ObjectTypes.INVOICE} id={invoiceId} />
            </div>
        </div>
    );
};

const InvoiceEditPage = () => {
    const { invoiceId } = useParams();
    const navigate = useBusinessNavigate();
    const isNew = !invoiceId || invoiceId === '0';

    const { data, isLoading, refetch } = useInvoiceDetailsQuery(isNew ? null : invoiceId);
    const invoice = data?.invoice || {};

    // Fetch invoice styles
    const { data: stylesData } = useInvoiceStylesQuery();
    const styles = stylesData?.styles || {};

    const [section, setSection] = useState('edit');
    const [questions, setQuestions] = useState([]);
    const [client, setClient] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
    });
    const [invoiceNumber, setInvoiceNumber] = useState(null);
    const [dueDate, setDueDate] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [showAddQuestion, setShowAddQuestion] = useState(false);
    const [addAfterQuestionId, setAddAfterQuestionId] = useState(undefined);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [showSendModal, setShowSendModal] = useState(false);
    const [showPaymentPanel, setShowPaymentPanel] = useState(false);
    const [isSavingBeforeSend, setIsSavingBeforeSend] = useState(false);
    const [isSavingBeforePayment, setIsSavingBeforePayment] = useState(false);
    const editQuestionPanelRef = useRef();
    useBlockUnsavedChanges(isDirty)

    // Initialize questions and client from invoice data
    useEffect(() => {
        if (isDirty) {
            return;
        }

        if (invoice?.questions) {
            setQuestions(invoice.questions);
        } else if (isNew && !isLoading) {
            // For new invoices, add default questions
            setQuestions([
                {
                    id: `temp-${Date.now()}-1`,
                    type: 'invoiceheader',
                    label: 'Invoice Header',
                    settings: {},
                    order: 0,
                },
                {
                    id: `temp-${Date.now()}-2`,
                    type: 'billinginfo',
                    label: 'Billing Info',
                    settings: {},
                    order: 1,
                },
                {
                    id: `temp-${Date.now()}-3`,
                    type: 'invoicelineitems',
                    label: 'Line Items',
                    settings: {
                        lineItems: [],
                    },
                    order: 2,
                },
                {
                    id: `temp-${Date.now()}-4`,
                    type: 'invoicesummary',
                    label: 'Invoice Summary',
                    settings: {
                        adjustments: [],
                    },
                    order: 3,
                },
            ]);
        }
        if (invoice?.client) {
            setClient({
                firstName: invoice.client.first_name || '',
                lastName: invoice.client.last_name || '',
                email: invoice.client.email || '',
                phone: invoice.client.phone || '',
            });
        }
        if (invoice?.invoice_number !== undefined) {
            setInvoiceNumber(invoice.invoice_number);
        }
        if (invoice?.due_at !== undefined) {
            setDueDate(invoice.due_at);
        }
    }, [invoice?.id, isNew, isLoading, invoice?.questions, invoice?.invoice_number, invoice?.due_at, isDirty]);

    const isEditable = isNew || !!invoice.is_editable;

    const { save, isSaving } = useBusinessPostApi(`/invoices/${invoiceId || 0}`);
    const { save: sendInvoice, isSaving: isSending } = useBusinessPostApi(`/invoices/${invoiceId}/send`);

    function doSave() {
        return save({
            questions,
            client,
            invoice_number: invoiceNumber,
            due_at: dueDate,
        }).then(result => {
            if (result) {
                setInvoiceDetailsQuery(result?.invoice?.id || invoiceId, result);
                setIsDirty(false);
                return result;
            } else {
                Toast.error('Failed to save invoice, please try again');
                return null;
            }
        });
    }

    function handleSave() {
        if (!isEditable) {
            Toast.error('This invoice is not editable');
            return;
        }

        doSave().then(result => {
            if (result) {
                Toast.success(isNew ? 'Invoice created successfully' : 'Invoice updated successfully');
                if (isNew && result?.invoice?.id) {
                    setTimeout(() => {
                        navigate(`/invoices/${result.invoice.id}`);
                    }, 10);
                }
            }
        });
    }

    function handleSendClick() {
        if (isDirty) {
            setIsSavingBeforeSend(true);
            doSave().then(result => {
                setIsSavingBeforeSend(false);
                if (result) {
                    setShowSendModal(true);
                }
            }).catch(() => setIsSavingBeforeSend(false));
        } else {
            setShowSendModal(true);
        }
    }

    function handlePaymentClick() {
        if (isDirty) {
            setIsSavingBeforePayment(true);
            doSave().then(result => {
                setIsSavingBeforePayment(false);
                if (result) {
                    setShowPaymentPanel(true);
                }
            }).catch(() => setIsSavingBeforePayment(false));
        } else {
            setShowPaymentPanel(true);
        }
    }

    function handleSend(additionalEmails) {
        sendInvoice({
            additional_emails: additionalEmails,
        }).then(result => {
            Toast.success('Invoice sent successfully');
            setShowSendModal(false);
            // Refetch invoice data to update sent_at timestamp
            refetch();
        });
    }

    function updateInvoice(updates) {
        if (!isEditable) {
            return false;
        }

        if (updates.invoice_number !== undefined) {
            setInvoiceNumber(updates.invoice_number);
            setIsDirty(true);
        }
        if (updates.due_at !== undefined) {
            setDueDate(updates.due_at);
            setIsDirty(true);
        }
    }

    function updateLocalQuestions(newQuestions)
    {
        if (!isEditable) {
            Toast.error('This invoice is not editable');
            return false;
        }

        setIsDirty(true)
        setQuestions(newQuestions);
    }

    function addQuestion({ type, label }) {
        const newQuestion = {
            id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for local state
            type,
            label: label || (Questions[type].dontDefaultLabelToType ? '' : (Questions[type]?.name || type)),
            settings: {},
            order: questions.length,
        };

        if (addAfterQuestionId !== undefined) {
            // Insert after specific question (or at beginning if null)
            if (addAfterQuestionId === null) {
                // Add at the beginning
                updateLocalQuestions([newQuestion, ...questions]);
            } else {
                const afterIndex = questions.findIndex(q => q.id === addAfterQuestionId);
                if (afterIndex !== -1) {
                    const newQuestions = [...questions];
                    newQuestions.splice(afterIndex + 1, 0, newQuestion);
                    updateLocalQuestions(newQuestions);
                } else {
                    updateLocalQuestions([...questions, newQuestion]);
                }
            }
            setAddAfterQuestionId(undefined);
        } else {
            // Add to end (when clicking the main "Add Question" button)
            updateLocalQuestions([...questions, newQuestion]);
        }

        setEditingQuestion(newQuestion)
        setShowAddQuestion(false);
    }

    function updateQuestion(questionToUpdate, updates) {
        const newQuestions = questions.map(q =>
            q.id === questionToUpdate.id ? { ...q, ...updates } : q
        );
        updateLocalQuestions(newQuestions);
    }

    function deleteQuestion(questionToDelete) {
        if (confirm('Are you sure you want to delete this?')) {
            updateLocalQuestions(questions.filter(q => q.id !== questionToDelete.id));
            setEditingQuestion(null);
        }
    }

    function onReorderQuestions(reorderedQuestions) {
        updateLocalQuestions(reorderedQuestions);
    }

    if (isLoading && !isNew) {
        return <Loading className="text-2xl mt-8" />;
    }

    return (
        <div>
            <PageHeading
                heading={
                    isNew ? (
                        'New Invoice'
                    ) : (
                        <div className="flex items-center gap-2">
                            <span>Invoice #{invoiceNumber}</span>
                            <div>
                                <InvoiceStatusBadge status={invoice.status} />
                            </div>
                        </div>
                    )
                }
                crumbs={[{ name: 'Invoices', href: '/invoices' }]}
            />

            {!isNew && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between max-w-4xl mb-4">
                    <SectionPicker
                        className="mb-4 sm:mb-0"
                        value={section}
                        onChange={setSection}
                        options={[
                            { value: 'edit', label: 'Edit' },
                            { value: 'activity', label: 'Activity' },
                        ]}
                    />

                    <div className="flex gap-2">
                        <a
                            href={`${invoice.view_link}?preview=true`}
                            target="_blank"
                            className="flex items-center btn-light-pill !px-3 hover:!px-6 uppercase font-semibold group"
                        >
                            <EyeIcon className="w-5 h-5" />
                            <span className="transition-all overflow-hidden max-w-0 opacity-0 group-hover:opacity-100 group-hover:max-w-24 group-hover:ml-2 text-nowrap">
                                Preview
                            </span>
                        </a>
                    </div>
                </div>
            )}

            {!!invoice.sent_at && isEditable && (
                <WarningMessageBlock className="mb-4">You’re editing an invoice that was already sent.</WarningMessageBlock>
            )}
            {!isEditable && (
                <WarningMessageBlock className="mb-4">This invoice can no longer be edited.</WarningMessageBlock>
            )}

            {section === 'edit' && (
                <InvoiceProvider
                    questions={questions}
                    invoice={{
                        ...invoice,
                        invoice_number: invoiceNumber,
                        due_at: dueDate,
                    }}
                    client={client}
                    setClient={setClient}
                    updateInvoice={updateInvoice}
                >
                    <FormBackgroundContainer styles={styles}>
                        <PageContainer styles={styles} centered={false} hidePoweredBy={true}>
                            <div className="pb-16">
                                <Section
                                    idx={false}
                                    section={{
                                        settings: { invoice: true },
                                        is_special: true,
                                    }}
                                    hasQuestions={true}
                                    onAddQuestion={() =>
                                        setShowAddQuestion(true)
                                    }
                                >
                                    <SortableList
                                        items={questions}
                                        onReorder={onReorderQuestions}
                                        renderItem={({ item: question }) => (
                                            <EditableQuestion
                                                key={question.id}
                                                question={question}
                                                formStyles={styles}
                                                isEditing={
                                                    editingQuestion?.id ===
                                                    question.id
                                                }
                                                setSettings={(newSettings) =>
                                                    updateQuestion(question, { settings: newSettings })
                                                }
                                                onEditQuestion={q =>
                                                    setEditingQuestion(q)
                                                }
                                                onAddQuestion={questionId => {
                                                    // Find the question immediately before this one
                                                    const currentIndex =
                                                        questions.findIndex(
                                                            q =>
                                                                q.id ===
                                                                questionId,
                                                        );
                                                    const previousQuestionId =
                                                        currentIndex > 0
                                                            ? questions[
                                                                  currentIndex -
                                                                      1
                                                              ].id
                                                            : null;
                                                    setAddAfterQuestionId(
                                                        previousQuestionId,
                                                    );
                                                    setShowAddQuestion(true);
                                                }}
                                                onDeleteQuestion={question => {
                                                        deleteQuestion(
                                                            question,
                                                        );
                                                }}
                                                showAddOnBottom={false}
                                            />
                                        )}
                                        containerClassName=""
                                    />
                                </Section>
                            </div>
                        </PageContainer>
                    </FormBackgroundContainer>

                    {/* Add Question Panel */}
                    <AddQuestionPanel
                        isOpen={showAddQuestion}
                        onClose={() => {
                            setShowAddQuestion(false);
                            setAddAfterQuestionId(undefined);
                        }}
                        onAddQuestion={addQuestion}
                        section={{ settings: { invoice: true } }}
                        isSaving={false}
                        hasScheduling={false}
                    />

                    {/* Edit Question Panel */}
                    <InvoiceEditQuestionPanel
                        ref={editQuestionPanelRef}
                        question={editingQuestion}
                        setQuestion={setEditingQuestion}
                        onUpdate={updateQuestion}
                        onDelete={deleteQuestion}
                    />

                    <div className="fixed z-1 pl-10 -ml-10 bottom-0 bg-gray-100 w-full py-2">
                        <div className="flex gap-x-8">
                            {isEditable && (
                                <button
                                    type="button"
                                    className="btn-save"
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? 'Saving...' : 'Save'}
                                </button>
                            )}

                            {!isNew && invoice.client_id && (
                                <div className={`flex gap-x-8`}>
                                    <button
                                        onClick={handleSendClick}
                                        className="btn-light-pill-lg flex items-center"
                                        disabled={isSavingBeforeSend}
                                    >
                                        <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                                        {isSavingBeforeSend
                                            ? 'Saving...'
                                            : invoice.status === 'sent' ||
                                                invoice.status === 'paid' ||
                                                invoice.status === 'overdue'
                                                ? 'Resend'
                                                : 'Send'}
                                    </button>

                                    {invoice.total > 0 && (
                                        <button
                                            onClick={handlePaymentClick}
                                            className="btn-light-pill-lg flex items-center"
                                            disabled={isSavingBeforePayment}
                                        >
                                            {isSavingBeforePayment ? 'Saving...' : 'Make a Payment'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </InvoiceProvider>
            )}

            {section === 'activity' && !isNew && (
                <ActivitySection invoiceId={invoiceId} invoice={invoice} />
            )}

            {/* Send Invoice Modal */}
            <SendInvoiceModal
                isOpen={showSendModal}
                onClose={() => setShowSendModal(false)}
                invoice={invoice}
                client={client}
                onConfirm={handleSend}
            />

            {/* Admin Payment Panel */}
            {!isNew && invoice.total > 0 && (
                <AdminPaymentPanel
                    isOpen={showPaymentPanel}
                    onClose={() => setShowPaymentPanel(false)}
                    onSave={() => {
                        // Refetch invoice data to update payment status
                        refetch();
                    }}
                    objectId={invoice.id}
                    objectType="invoice"
                    totalPrice={invoice.total || 0}
                    totalPaid={
                        invoice.payments?.reduce(
                            (sum, p) => sum + (p.amount || 0),
                            0,
                        ) || 0
                    }
                />
            )}
        </div>
    );
};

export default InvoiceEditPage;

